import { useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { useT } from "../../i18n/LanguageProvider";
import { isShelfZone } from "../../utils/shelfZones";
import { createPortal } from "react-dom";
import { shopModeApi, type FloorBox } from "../../api/shopMode";
import type { Zone } from "../../api/types";
import { ShopFloorBoxLabelPrint } from "./ShopFloorBoxLabelPrint";

/**
 * G-84 (OD-46, OD-48, OD-49) — "Create a new box". Anyone PIN'd in may. An
 * optional name and an optional kept-in shelf; the code is generated. The
 * label is offered straight away, because the box is empty and unlabelled
 * until it is, and the printer is right there.
 *
 * Portalled to the body: the stock panel this opens from carries a
 * `backdrop-filter` that traps a fixed overlay (G-73).
 */
export function ShopFloorNewBox({
  shelfZones,
  onClose,
  onCreated,
}: {
  shelfZones: Zone[];
  onClose(): void;
  onCreated(box: FloorBox): void;
}) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [keptInZoneId, setKeptInZoneId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<FloorBox | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const box = await shopModeApi.floorIssueBox({
        name: name.trim() || null,
        keptInZoneId: keptInZoneId || null,
      });
      setCreated(box);
      onCreated(box);
    } catch (err: unknown) {
      setError(errorText(err, "box.couldNotCreate"));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="sf-bouquet-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={
        created ? t("box.boxCodeAria", { code: created.code }) : t("box.createNew")
      }
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sf-bouquet-modal sf-box-modal" onClick={(e) => e.stopPropagation()}>
        {created ? (
          <>
            <h4 className="sf-bouquet-modal-title">
              {created.code}
              {created.name ? <span className="sf-box-modal-subtitle"> · {created.name}</span> : null}
            </h4>
            <p className="muted">{t("box.createdHint")}</p>
            <ShopFloorBoxLabelPrint box={created} />
            <div className="sf-item-card-panel-actions">
              <button type="button" className="button shop-floor-action-btn" onClick={onClose}>
                {t("box.done")}
              </button>
            </div>
          </>
        ) : (
          <>
            <h4 className="sf-bouquet-modal-title">{t("box.createNew")}</h4>
            {error ? <div className="alert error">{error}</div> : null}
            <div className="sf-item-card-panel">
              {/*
                G-98: no code to show here — it is generated server-side at
                issue time, so this form cannot put "BOX-000017" in the
                placeholder the way Rename does. The words carry the same
                promise instead.
              */}
              <label htmlFor="sf-new-box-name">{t("box.nameOptional")}</label>
              <input
                id="sf-new-box-name"
                type="text"
                maxLength={80}
                placeholder={t("box.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
              <label htmlFor="sf-new-box-kept">{t("box.keptInOptional")}</label>
              <select
                id="sf-new-box-kept"
                value={keptInZoneId}
                onChange={(e) => setKeptInZoneId(e.target.value)}
              >
                <option value="">{t("box.notRecorded")}</option>
                {shelfZones
                  .filter((z) => z.isActive && isShelfZone(z))
                  .map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
              </select>
              <div className="sf-item-card-panel-actions">
                <button type="button" className="button plain" disabled={busy} onClick={onClose}>
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  className="button shop-floor-action-btn"
                  disabled={busy}
                  onClick={() => void submit()}
                >
                  {busy ? t("box.creating") : t("box.createBox")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
